import React from 'react';
import { useTranslation } from 'react-i18next';
import { Star, ThumbsUp, ThumbsDown, CheckCircle } from 'lucide-react';
import { Button } from '../ui/button';

const ReviewCard = ({ review, onHelpful, userVote }) => {
  const { t, i18n } = useTranslation();
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    // Use current i18next language so the date matches the UI language.
    return date.toLocaleDateString(i18n.language || 'es', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, i) => (
      <Star
        key={i}
        className={`w-4 h-4 ${
          i < rating 
            ? 'fill-yellow-400 text-yellow-400' 
            : 'text-gray-300'
        }`}
      />
    ));
  };

  return (
    <div className="border-b border-gray-200 py-6 last:border-b-0">
      <div className="flex items-start gap-4">
        <div className="flex-shrink-0">
          <div className="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center text-gray-600 font-semibold">
            {review.userName.charAt(0).toUpperCase()}
          </div>
        </div>
        
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <div className="flex items-center gap-1">
              {renderStars(review.rating)}
            </div>
            {review.verifiedPurchase && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-800 text-xs font-medium rounded">
                <CheckCircle className="w-3 h-3" />
                {t('productReviews.verifiedPurchase')}
              </span>
            )}
          </div>
          
          <h4 className="font-semibold text-gray-900 mb-1">{review.title}</h4>
          
          <div className="flex items-center gap-2 text-sm text-gray-600 mb-3">
            <span className="font-medium">{review.userName}</span>
            <span>•</span>
            <span>{formatDate(review.createdAt)}</span>
          </div>
          
          <p className="text-gray-700 mb-3 whitespace-pre-wrap">{review.review}</p>
          
          {review.photos && review.photos.length > 0 && (
            <div className="flex gap-2 mb-3">
              {review.photos.map((photo, index) => (
                <img
                  key={index}
                  src={photo}
                  alt={t('productReviews.photoAlt', { index: index + 1 })}
                  className="w-20 h-20 object-cover rounded border border-gray-200"
                />
              ))}
            </div>
          )}
          
          <div className="flex items-center gap-4 text-sm">
            <button
              onClick={() => onHelpful && onHelpful(review.id, true)}
              className={`flex items-center gap-1 ${
                userVote === true 
                  ? 'text-green-600 font-medium' 
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <ThumbsUp className="w-4 h-4" />
              {t('productReviews.helpful')} ({review.helpfulCount})
            </button>
            
            <button
              onClick={() => onHelpful && onHelpful(review.id, false)}
              className={`flex items-center gap-1 ${
                userVote === false 
                  ? 'text-red-600 font-medium' 
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <ThumbsDown className="w-4 h-4" />
              {t('productReviews.notHelpful')} ({review.notHelpfulCount})
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReviewCard;
